import { useCallback, useEffect, useState } from "react";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CheckCircle2, Download, Loader2 } from "lucide-react";

interface Props {
  userId: string | null;
  memberName: string;
  value?: string;
  /** When present, the signed copy is filed onto the member's own record too. */
  memberRecordId?: string | null;
  onIntegrated?: () => void;
}

type StepRow = { id: string; step_number: number; title: string; action_config: any };
type DocRow = {
  id: string;
  document_name: string;
  file_path: string;
  created_at: string;
  review_status: string;
};

const SECTION_KEY = "affiliation_agreement";

// The team side of the member's Affiliation Agreement sequence: see the signed
// copy the member sent, then mark the last step integrated.
export default function AffiliationAgreementCard({
  userId,
  memberName,
  value = "affiliation",
  memberRecordId,
  onIntegrated,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState<StepRow[]>([]);
  const [progress, setProgress] = useState<Record<string, { status: string; completed_at: string | null }>>({});
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data: stepRows } = await supabase
      .from("journey_steps")
      .select("id, step_number, title, action_config")
      .eq("section_key", SECTION_KEY)
      .eq("is_active", true)
      .order("step_number");

    const ids = (stepRows || []).map((s: any) => s.id);
    const [{ data: progressRows }, { data: docRows }] = await Promise.all([
      ids.length
        ? supabase
            .from("user_journey_step_progress")
            .select("step_id, status, completed_at")
            .eq("user_id", userId)
            .in("step_id", ids)
        : Promise.resolve({ data: [] as any[] }),
      supabase
        .from("user_journey_documents")
        .select("id, document_name, file_path, created_at, review_status")
        .eq("user_id", userId)
        .eq("document_type", "affiliation_agreement")
        .order("created_at", { ascending: false }),
    ]);

    setSteps((stepRows || []) as StepRow[]);
    setProgress(
      Object.fromEntries(
        ((progressRows || []) as any[]).map((p) => [
          p.step_id,
          { status: p.status, completed_at: p.completed_at },
        ]),
      ),
    );
    setDocs((docRows || []) as DocRow[]);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDoc = async (path: string) => {
    const { data, error } = await supabase.storage
      .from("journey-documents")
      .createSignedUrl(path, 300);
    if (error || !data?.signedUrl) {
      toast.error("That file would not open. Try again in a moment.");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const markIntegrated = async () => {
    const teamStep = steps.find((s) => s.action_config?.mode === "team_confirm");
    if (!teamStep || !userId) return;
    setBusy(true);
    const now = new Date().toISOString();
    const { error } = await supabase.from("user_journey_step_progress").upsert(
      {
        user_id: userId,
        step_id: teamStep.id,
        status: "completed",
        completed_at: now,
        verified_completion: true,
        action_data: { integrated_by_team: true } as any,
      } as any,
      { onConflict: "user_id,step_id" },
    );

    if (!error && docs[0]) {
      await supabase
        .from("user_journey_documents")
        .update({ review_status: "integrated", reviewed_at: now } as any)
        .eq("id", docs[0].id);
    }

    // The record itself carries the instrument, not just the portal machinery.
    if (!error && memberRecordId) {
      await (supabase as any)
        .from("member_records")
        .update({
          affiliation_document_path: docs[0]?.file_path ?? null,
          affiliation_integrated_at: now,
        })
        .eq("id", memberRecordId);
    }
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Marked integrated for ${memberName}`);
    void load();
    onIntegrated?.();
  };

  if (!userId) return null;

  const teamStep = steps.find((s) => s.action_config?.mode === "team_confirm");
  const integrated = teamStep ? progress[teamStep.id]?.status === "completed" : false;
  const doneCount = steps.filter((s) => progress[s.id]?.status === "completed").length;

  return (
    <AccordionItem value={value}>
      <AccordionTrigger className="py-3 text-base">
        Affiliation Agreement ({doneCount} of 5 done)
      </AccordionTrigger>
      <AccordionContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              The member reads it, signs it, scans it, and sends it. The last step is ours.
            </p>
            <ul className="text-sm">
              {steps.map((s) => {
                const done = progress[s.id]?.status === "completed";
                return (
                  <li key={s.id} className="flex items-center justify-between gap-3 py-1">
                    <span className={done ? "text-green-700" : "text-muted-foreground"}>
                      {s.title}
                    </span>
                    <Badge variant={done ? "default" : "secondary"} className="text-xs">
                      {done ? "Done" : "Not yet"}
                    </Badge>
                  </li>
                );
              })}
              {steps.length === 0 && (
                <li className="py-1 text-sm text-muted-foreground">No steps set up yet.</li>
              )}
            </ul>

            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">Signed copies sent</div>
              {docs.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing sent yet.</p>
              ) : (
                docs.map((d) => (
                  <div key={d.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <Button size="sm" variant="outline" onClick={() => void openDoc(d.file_path)}>
                      <Download className="mr-2 h-3.5 w-3.5" />
                      {d.document_name}
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {new Date(d.created_at).toLocaleDateString()}
                      {d.review_status === "awaiting_team_review" ? " (waiting on us)" : ""}
                    </span>
                  </div>
                ))
              )}
            </div>

            {integrated ? (
              <div className="flex items-center gap-2 text-sm text-green-700">
                <CheckCircle2 className="h-4 w-4" /> Filed with the member's record.
              </div>
            ) : (
              <Button size="sm" onClick={() => void markIntegrated()} disabled={busy || !teamStep}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Mark it integrated
              </Button>
            )}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
